import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useNavigate } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import { MODULE_EMOJIS } from "@/lib/module-labels";

interface Notification {
  id: string;
  title: string;
  body: string;
  module_key: string | null;
  type: string;
  read: boolean;
  created_at: string;
}

export default function NotificationBell({ label }: { label?: string } = {}) {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false })
      .limit(20);
    if (data) setNotifications(data as Notification[]);
  };

  const channelIdRef = useRef(`notifications-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    fetchNotifications();
    const channel = supabase
      .channel(channelIdRef.current)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" }, () => fetchNotifications())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const openSupportChat = (tab: "support" | "vip") => {
    if (window.location.pathname !== "/dashboard") navigate("/dashboard");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("open-support-chat", { detail: { tab } }));
    }, 150);
  };

  const handleClick = async (notif: Notification) => {
    if (!notif.read) {
      await supabase.from("notifications").update({ read: true }).eq("id", notif.id);
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
    }
    setOpen(false);
    if (notif.type === "sherpa_reply") { openSupportChat("vip"); return; }
    if (notif.type === "support_reply") { openSupportChat("support"); return; }
    // Las ayudas guardan el código de la subvención en module_key: no es un
    // módulo del roadmap, así que se navega al panel de subvenciones.
    if (notif.type === "subvencion") { navigate("/panel/subvenciones"); return; }
    if (notif.module_key) {
      navigate("/dashboard");
      setTimeout(() => {
        const el = document.getElementById(`module-${notif.module_key}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2000);
        }
      }, 300);
    }
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from("notifications").update({ read: true }).in("id", unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const dateLocale = lang === "es" ? es : enUS;

  return (
    <div className="flex items-center gap-1">
      {/* Notifications */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className={label ? "relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-radar-nav-ink hover:bg-radar-nav-2 transition-colors" : "relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-xs hover:bg-secondary transition-colors"} aria-label={label ?? t("notif", "title")}>
            <Bell className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-display text-sm font-semibold">{t("notif", "title")}</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[11px] font-body text-foreground hover:underline">
                {t("notif", "markAllRead")}
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground font-body">{t("notif", "none")}</p>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0 ${!notif.read ? "bg-primary/5" : ""}`}
                  aria-label={`${notif.read ? "" : (lang === "es" ? "Sin leer: " : "Unread: ")}${notif.title}`}
                >
                  <span className="text-lg shrink-0" aria-hidden="true">
                    {notif.module_key ? (MODULE_EMOJIS[notif.module_key] ?? "🍋") : "🔔"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-body truncate ${!notif.read ? "font-semibold" : ""}`}>{notif.title}</p>
                    <p className="text-xs text-muted-foreground font-body truncate">{notif.body}</p>
                    <p className="text-[10px] text-muted-foreground/70 font-body mt-0.5">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: dateLocale })}
                    </p>
                  </div>
                  {!notif.read && <span className="shrink-0 mt-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
